/**
 * `grnStatus` → operator-facing label + StatusPill tone.
 *
 * The GRN wire enum has three values (draft · posted · void). The list
 * surfaces a synthetic "with shortage" facet (posted rows whose
 * `rejectedLineCount > 0`) that isn't a real enum value — it's derived
 * client-side and rendered with the same `held` chrome the prototype uses.
 *
 * @see packages/api-contract/src/procedures/goods-receipts.ts (grnStatus)
 */
import type { StatusTone } from "@flint/ui";

export type GrnStatus = "draft" | "posted" | "void";

const META: Record<GrnStatus, { label: string; tone: StatusTone }> = {
  draft: { label: "Draft", tone: "muted" },
  posted: { label: "Posted", tone: "ok" },
  void: { label: "Void", tone: "muted" },
};

export function grnStatusLabel(status: GrnStatus): string {
  return META[status]?.label ?? status;
}

export function grnStatusTone(status: GrnStatus): StatusTone {
  return META[status]?.tone ?? "muted";
}

/**
 * The list-row pill — a posted GRN that has rejected/short lines reads
 * "Posted · shortage" with warn tone (the prototype's `.status.held`),
 * everything else uses the plain status label.
 */
export function grnRowPill(
  status: GrnStatus,
  rejectedLineCount: number,
): { label: string; tone: StatusTone } {
  if (status === "posted" && rejectedLineCount > 0) {
    return { label: "Posted · shortage", tone: "warn" };
  }
  return { label: grnStatusLabel(status), tone: grnStatusTone(status) };
}
